import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Plug, Plus } from 'lucide-react';
import { api } from '@/lib/api';
import { usePermission } from '@/Hooks/usePermission';
import { useBranches } from '@/Pages/Operations/shared';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Dialog } from '@/components/ui/dialog';
import { Field, Select } from '@/components/ui/field';
import { Icon } from '@/components/ui/icon';
import { Input } from '@/components/ui/input';
import { Table, TableWrap, TD, TH, THead, TR } from '@/components/ui/table';
import PanelResult from './PanelResult';
import {
  INTEGRATIONS_PERMISSIONS,
  INTEGRATIONS_ROUTES,
  invalidateIntegrationInstallation,
  useIntegrationProvidersQuery,
  type InstallIntegrationBody,
  type IntegrationProvider,
} from '../integrationsApi';
import { apiMessage } from '../lib';
import { useAppDispatch } from '@/Redux/store';

const CATEGORIES = ['PAYMENTS', 'ACCOUNTING', 'DELIVERY', 'MESSAGING', 'FISCAL', 'OTHER'] as const;

/**
 * Screen 1 — the provider catalogue, as this venue sees it. A provider gated
 * behind a feature flag the venue does not hold is already omitted server
 * side (`IntegrationsService.listProviders`), so nothing here re-implements
 * that filter — every row shown is genuinely installable.
 */
export default function ProvidersPanel() {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { can } = usePermission();
  const canInstall = can(INTEGRATIONS_PERMISSIONS.install);

  const [category, setCategory] = useState('');
  const [search, setSearch] = useState('');
  const [installing, setInstalling] = useState<IntegrationProvider | null>(null);

  const query = {
    limit: 100,
    ...(category ? { category: category as IntegrationProvider['category'] } : {}),
    ...(search.trim() ? { search: search.trim() } : {}),
  };
  const { data, isFetching, isError, error, refetch } = useIntegrationProvidersQuery(query);

  const filtered = Boolean(category || search.trim());
  const clearFilters = () => {
    setCategory('');
    setSearch('');
  };

  const onInstalled = () => {
    dispatch(invalidateIntegrationInstallation());
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div className="flex flex-wrap items-end gap-3">
          <Field label={t('integrations.providers.filterCategory')} controlId="int-category">
            <Select value={category} onChange={(e) => setCategory(e.target.value)} className="w-48">
              <option value="">{t('integrations.providers.filterAllCategories')}</option>
              {CATEGORIES.map((c) => (
                <option key={c} value={c}>
                  {t(`integrations.category.${c}`)}
                </option>
              ))}
            </Select>
          </Field>
          <Field label={t('integrations.providers.search')} controlId="int-search">
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={t('integrations.providers.search')}
              className="w-56"
            />
          </Field>
          {filtered && (
            <Button variant="outline" onClick={clearFilters}>
              {t('integrations.states.clearFilters')}
            </Button>
          )}
        </div>
      </div>

      <PanelResult
        loading={isFetching}
        error={isError ? apiMessage(error, t('integrations.states.loadFailed')) : null}
        count={data?.items.length ?? 0}
        onRetry={refetch}
        filtered={filtered}
        onClearFilters={clearFilters}
        emptyTitle={t('integrations.providers.emptyTitle')}
        emptyHint={t('integrations.providers.emptyHint')}
      >
        <TableWrap>
          <Table>
            <THead>
              <TR>
                <TH>{t('integrations.providers.name')}</TH>
                <TH>{t('integrations.providers.category')}</TH>
                <TH>{t('integrations.providers.auth')}</TH>
                <TH>{t('integrations.providers.capabilities')}</TH>
                <TH className="text-end">{t('common.actions')}</TH>
              </TR>
            </THead>
            <tbody>
              {(data?.items ?? []).map((provider) => (
                <TR key={provider.id}>
                  <TD>
                    <div className="flex items-center gap-2">
                      <Icon as={Plug} className="text-ink-muted" />
                      <div>
                        <p className="font-medium">{provider.name}</p>
                        <p className="text-xs text-ink-muted">{provider.key}</p>
                      </div>
                    </div>
                  </TD>
                  <TD>
                    <Badge tone="brand">{t(`integrations.category.${provider.category}`)}</Badge>
                  </TD>
                  <TD className="text-sm text-ink-muted">{provider.authKind}</TD>
                  <TD className="text-sm text-ink-muted">
                    {provider.capabilities.join(', ') || '—'}
                  </TD>
                  <TD className="text-end">
                    {canInstall && (
                      <Button size="sm" variant="outline" onClick={() => setInstalling(provider)}>
                        <Icon as={Plus} />
                        {t('integrations.providers.install')}
                      </Button>
                    )}
                  </TD>
                </TR>
              ))}
            </tbody>
          </Table>
        </TableWrap>
      </PanelResult>

      <InstallDialog
        provider={installing}
        onClose={() => setInstalling(null)}
        onInstalled={onInstalled}
      />
    </div>
  );
}

function InstallDialog({
  provider,
  onClose,
  onInstalled,
}: {
  provider: IntegrationProvider | null;
  onClose: () => void;
  onInstalled: () => void;
}) {
  const { t } = useTranslation();
  const branches = useBranches();
  const [branchId, setBranchId] = useState('');
  const [scopes, setScopes] = useState<string[]>([]);
  const [events, setEvents] = useState<string[]>([]);
  const [endpointUrl, setEndpointUrl] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const reset = () => {
    setBranchId('');
    setScopes([]);
    setEvents([]);
    setEndpointUrl('');
    setError(null);
  };

  const toggle = (list: string[], set: (v: string[]) => void, value: string) => {
    set(list.includes(value) ? list.filter((v) => v !== value) : [...list, value]);
  };

  const submit = async () => {
    if (!provider) return;
    setBusy(true);
    setError(null);
    try {
      const body: InstallIntegrationBody = {
        providerKey: provider.key,
        ...(branchId ? { branchId } : {}),
        ...(scopes.length ? { scopes } : {}),
        ...(events.length ? { subscribedEvents: events } : {}),
        ...(endpointUrl.trim() ? { endpointUrl: endpointUrl.trim() } : {}),
      };
      await api.post(INTEGRATIONS_ROUTES.installations, body);
      toast.success(t('integrations.providers.installed', { name: provider.name }));
      onInstalled();
      reset();
      onClose();
    } catch (requestError) {
      setError(apiMessage(requestError, t('integrations.errors.actionFailed')));
    } finally {
      setBusy(false);
    }
  };

  if (!provider) return null;

  return (
    <Dialog
      open
      onClose={() => {
        reset();
        onClose();
      }}
      title={t('integrations.providers.installTitle', { name: provider.name })}
      description={provider.description ?? undefined}
      className="max-w-lg"
      footer={
        <div className="flex justify-end gap-2">
          <Button variant="secondary" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button disabled={busy} onClick={() => void submit()}>
            {t('integrations.providers.install')}
          </Button>
        </div>
      }
    >
      <div className="space-y-4">
        <p className="text-sm text-ink-muted">{t('integrations.providers.installLandsPending')}</p>

        {branches.length > 0 && (
          <Field
            label={t('integrations.common.branch')}
            hint={t('integrations.providers.branchHint')}
          >
            <Select value={branchId} onChange={(e) => setBranchId(e.target.value)}>
              <option value="">{t('integrations.providers.wholeVenue')}</option>
              {branches.map((b) => (
                <option key={b.id} value={b.id}>
                  {b.name}
                </option>
              ))}
            </Select>
          </Field>
        )}

        {provider.scopes.length > 0 && (
          <Field label={t('integrations.providers.scopes')}>
            <div className="flex flex-wrap gap-2">
              {provider.scopes.map((scope) => (
                <label
                  key={scope}
                  className="flex items-center gap-1.5 rounded-lg border px-2.5 py-1.5 text-sm"
                >
                  <input
                    type="checkbox"
                    checked={scopes.includes(scope)}
                    onChange={() => toggle(scopes, setScopes, scope)}
                  />
                  {scope}
                </label>
              ))}
            </div>
          </Field>
        )}

        {provider.events.length > 0 && (
          <Field
            label={t('integrations.providers.subscribedEvents')}
            hint={t('integrations.providers.subscribedEventsHint')}
          >
            <div className="flex max-h-40 flex-wrap gap-2 overflow-y-auto">
              {provider.events.map((event) => (
                <label
                  key={event}
                  className="flex items-center gap-1.5 rounded-lg border px-2.5 py-1.5 text-sm"
                >
                  <input
                    type="checkbox"
                    checked={events.includes(event)}
                    onChange={() => toggle(events, setEvents, event)}
                  />
                  {event}
                </label>
              ))}
            </div>
          </Field>
        )}

        {provider.capabilities.includes('outbound') && (
          <Field
            label={t('integrations.providers.endpointUrl')}
            hint={t('integrations.providers.endpointUrlHint')}
          >
            <Input
              value={endpointUrl}
              onChange={(e) => setEndpointUrl(e.target.value)}
              placeholder="https://"
            />
          </Field>
        )}

        {provider.requiredCredentials.length > 0 && (
          <p className="text-sm text-ink-muted">
            {t('integrations.providers.requiredCredentials', {
              kinds: provider.requiredCredentials.join(', '),
            })}
          </p>
        )}

        {error && (
          <p role="alert" className="text-sm text-red-600 dark:text-red-400">
            {error}
          </p>
        )}
      </div>
    </Dialog>
  );
}
